import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import WayForPayWidget from "pages/OrderModal/components/WayForPayWidget";
import ConfirmModal from "components/ConfirmModal";
import MoneyIcon from "@mui/icons-material/Money";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import CheckIcon from "@mui/icons-material/Check";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DiscountOutlinedIcon from "@mui/icons-material/DiscountOutlined";
import AddIcon from "@mui/icons-material/Add";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { setCard, setPayment } from "store/slices/payment";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import { shallowEqual } from "react-redux";
import {
  useGetClientCardsQuery,
  useGetClientProfileQuery,
  useGetWayForPayDataQuery,
  useRemoveClientCardMutation,
} from "store/api";
import { useAppSelector } from "store/hooks/useAppSelector";
import {
  maskCardNumber,
  maxClientCardsCount,
  paymentTypes,
  shortCardNumber,
} from "./constants";
import { v4 as uuidv4 } from "uuid";
import { ClientCardsState, setClientCards } from "store/slices/clientCards";
import useNotificationToast from "hooks/useNotificationToast/useNotificationToast";
import styles from "pages/OrderModal/components/DetailsSection/DetailsSection.styled";

const PaymentSection = () => {
  const auth = useAppSelector((state) => state.auth.token, shallowEqual);
  const cost = useAppSelector(
    (state) => state.orderCost.costDetails,
    shallowEqual,
  );
  const payment = useAppSelector((state) => state.payment, shallowEqual);
  const settings = useAppSelector((state) => state.settings, shallowEqual);
  const cards = useAppSelector((state) => state.clientCards, shallowEqual);
  const [isCashChecked, setIsCashChecked] = useState<boolean>(false);
  const [isCardChecked, setIsCardChecked] = useState<boolean>(false);
  const [isBonusChecked, setIsBonusChecked] = useState<boolean>(false);
  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [removeClientCard] = useRemoveClientCardMutation();
  const { data: clientData, isSuccess } = useGetClientProfileQuery(undefined, {
    skip: !auth,
  });
  const { data: paymentHTML, refetch } = useGetWayForPayDataQuery(undefined, {
    skip: !auth || !isWidgetOpen,
  });
  const {
    data: clientCards,
    isSuccess: isCardsSuccess,
    refetch: cardsRefetch,
  } = useGetClientCardsQuery(undefined, {
    skip: !auth,
  });

  useEffect(() => {
    const originalLog = console.log;

    console.log = function (...args) {
      originalLog.apply(console, args);
      if (args.includes("Payment Approved!")) {
        cardsRefetch();
      }
    };

    return () => {
      console.log = originalLog;
    };
  }, [cardsRefetch]);

  useEffect(() => {
    if (isCardsSuccess && clientCards) {
      dispatch(setClientCards(clientCards));
    }
  }, [isCardsSuccess, clientCards]);

  useEffect(() => {
    setIsCashChecked(paymentTypes[0].title === payment.title);
    setIsBonusChecked(paymentTypes[1].title === payment.title);
    setIsCardChecked(paymentTypes[2].title === payment.title);
  }, [payment]);

  const handlePaymentClick = () => {
    if (!auth) {
      setOpenOptions(false);
    } else {
      setOpenOptions(!openOptions);
    }
  };

  const handleCashClick = () => {
    dispatch(setPayment(paymentTypes[0]));
    setOpenOptions(!openOptions);
    setIsWidgetOpen(false);
  };

  const handleCardClick = (card: ClientCardsState) => () => {
    dispatch(setPayment(paymentTypes[2]));
    dispatch(setCard(card));
    setOpenOptions(!openOptions);
  };

  const handleAddCardClick = () => {
    setOpenOptions(!openOptions);
    setIsWidgetOpen(true);
    if (isWidgetOpen) {
      refetch();
    } else {
      setIsWidgetOpen(true);
    }
  };

  const handleBonusClick = () => {
    dispatch(setPayment(paymentTypes[1]));
    setOpenOptions(!openOptions);
    setIsWidgetOpen(false);
  };

  const handleRemoveCard = (card: ClientCardsState) => async () => {
    try {
      await removeClientCard(card.cardGuid).unwrap();
      cardsRefetch();
      useNotificationToast({
        type: "success",
        message: "Картка успішно видалена",
      });
    } catch (error) {
      useNotificationToast({
        type: "error",
      });
    } finally {
      setIsConfirmModalOpen((prev) => !prev);
    }
  };

  const handleToggleConfirmModalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsConfirmModalOpen((prev) => !prev);
  };

  const handleToggleConfirmModal = () => {
    setIsConfirmModalOpen((prev) => !prev);
  };

  return (
    <>
      <ListItemButton onClick={handlePaymentClick} sx={styles.itemBtn}>
        <ListItemIcon>
          <MoneyIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <>
              Спосіб оплати:&nbsp;
              <Typography component="span" sx={styles.time(true)}>
                {payment.title === paymentTypes[2].title
                  ? shortCardNumber(payment.card.cardMask)
                  : payment.title}
              </Typography>
            </>
          }
        />
      </ListItemButton>
      <Collapse
        in={openOptions}
        timeout="auto"
        unmountOnExit
        sx={styles.innerList}
      >
        <List component="div" disablePadding>
          <ListItemButton
            sx={styles.innerItem(isCashChecked)}
            onClick={handleCashClick}
          >
            <ListItemIcon>
              <LocalAtmIcon />
            </ListItemIcon>
            <ListItemText primary={paymentTypes[0].title} />
            <ListItemIcon sx={styles.innerEndIcon(isCashChecked)}>
              <CheckIcon />
            </ListItemIcon>
          </ListItemButton>
          {isSuccess && (
            <ListItemButton
              sx={styles.innerItem(isBonusChecked)}
              onClick={handleBonusClick}
              disabled={
                !clientData.client_bonuses ||
                !cost.order_cost ||
                clientData.client_bonuses < +cost.order_cost
              }
            >
              <ListItemIcon>
                <DiscountOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary={`${paymentTypes[1].title} (${
                  clientData.client_bonuses + settings.currency
                })`}
              />
              <ListItemIcon sx={styles.innerEndIcon(isBonusChecked)}>
                <CheckIcon />
              </ListItemIcon>
            </ListItemButton>
          )}
          {cards.length
            ? cards.map((card) => (
                <Fragment key={uuidv4()}>
                  <ListItemButton
                    sx={styles.innerItem(isCardChecked)}
                    onClick={handleCardClick(card)}
                  >
                    <ListItemIcon>
                      <CreditCardIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <>
                          <Typography component="span">Карта:&nbsp;</Typography>
                          <Typography component="span" sx={styles.card}>
                            {maskCardNumber(card.cardMask)}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemIcon
                      onClick={handleToggleConfirmModalClick}
                      sx={styles.deleteIcon(isCardChecked)}
                    >
                      <CancelOutlinedIcon />
                    </ListItemIcon>
                    {isCardChecked && (
                      <ListItemIcon sx={styles.innerEndIcon(isCardChecked)}>
                        <CheckIcon />
                      </ListItemIcon>
                    )}
                  </ListItemButton>
                  <ConfirmModal
                    openModal={isConfirmModalOpen}
                    modalClose={handleToggleConfirmModal}
                    handleAction={handleRemoveCard(card)}
                    titleMessage="Ви дійсно хочете видалити картку?"
                    buttonMessage="Так"
                    secondBtnMessage="Ні"
                  />
                </Fragment>
              ))
            : null}
          {cards.length <= maxClientCardsCount && (
            <Box sx={styles.addCard} onClick={handleAddCardClick}>
              <AddIcon />
              Додати картку
            </Box>
          )}
        </List>
      </Collapse>
      {isWidgetOpen && paymentHTML && (
        <WayForPayWidget htmlContent={paymentHTML} />
      )}
    </>
  );
};

export default PaymentSection;
